import { useState } from 'react';

import {
	Autocomplete,
	Button,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';

import { useLoggedUser } from 'hooks';
import roles from 'common/constants/roles';
import assignmentStatus from 'common/constants/assignmentStatus';
import Tools from 'utils/Tools';
import { ActionButtons } from 'components';

const AssignmentFilterForm = ({ onSubmit, clients, projects, writers = [], editors = [] }) => {
	const [filteredProjects, setFilteredProjects] = useState(null);

	const { loggedUser } = useLoggedUser();

	const isAdminOrAssistant = [roles.ADMIN.value, roles.ASSISTANT.value].includes(loggedUser.role);

	const formik = useFormik({
		initialValues: {
			clientId: '',
			projectId: '',
			editorId: '',
			status: '',
			title: '',
			writerId: '',
		},
		onSubmit,
	});

	const handleChangeClient = (clientIdValue) => {
		if (clientIdValue) {
			const clientProjects = projects.filter(({ clientId }) => clientId === clientIdValue);
			setFilteredProjects(clientProjects);
			if (!clientProjects.some(({ id }) => id === formik.values.projectId)) {
				formik.setFieldValue('projectId', '');
			}
		} else {
			setFilteredProjects(null);
		}
	};

	const filterUsers = (options, { inputValue }) =>
		options.filter(({ name, lastName }) => Tools.matchesSearchTerm(inputValue, [name, lastName]));

	const projectOptions = filteredProjects ?? projects;

	const handleClearFilters = () => {
		if (filteredProjects) setFilteredProjects(null);
		formik.resetForm({
			values: {
				title: '',
				clientId: '',
				writerId: '',
				editorId: '',
				projectId: '',
				status: '',
			},
		});
		onSubmit({});
	};

	return (
		<form onSubmit={formik.handleSubmit} className="AssignmentFilterForm">
			<TextField
				fullWidth
				id="title"
				name="title"
				label="Título de la asignación"
				value={formik.values.title}
				onChange={formik.handleChange}
			/>
			{isAdminOrAssistant && (
				<FormControl fullWidth>
					<Autocomplete
						id="writerId"
						options={writers}
						getOptionLabel={(option) => Tools.getFullName(option)}
						isOptionEqualToValue={(option, value) => option.id === value?.id}
						filterOptions={filterUsers}
						noOptionsText="Sin resultados"
						onChange={(_, newValue) => formik.setFieldValue('writerId', newValue?.id || '')}
						value={writers.find(({ id }) => id === formik.values.writerId) || null}
						renderInput={(params) => <TextField {...params} label="Redactor" />}
					/>
					<FormHelperText error>{formik.touched.writerId && formik.errors.writerId}</FormHelperText>
				</FormControl>
			)}
			{isAdminOrAssistant && (
				<FormControl fullWidth>
					<InputLabel id="editorId-label">Editor</InputLabel>
					<Select
						labelId="editorId-label"
						id="editorId"
						label="Editor"
						name="editorId"
						onChange={(e) => formik.setFieldValue('editorId', e.target.value)}
						value={formik.values.editorId}
					>
						<MenuItem value="">- Todos -</MenuItem>
						{editors.map(({ id, name, lastName }) => (
							<MenuItem key={id} value={id}>
								{Tools.getFullName({ name, lastName })}
							</MenuItem>
						))}
					</Select>
					<FormHelperText error>{formik.touched.editorId && formik.errors.editorId}</FormHelperText>
				</FormControl>
			)}
			<FormControl fullWidth>
				<Autocomplete
					id="clientId"
					options={clients}
					getOptionLabel={(option) => Tools.getFullName(option)}
					isOptionEqualToValue={(option, value) => option.id === value?.id}
					filterOptions={filterUsers}
					noOptionsText="Sin resultados"
					onChange={(_, newValue) => {
						const clientIdValue = newValue?.id || '';
						formik.setFieldValue('clientId', clientIdValue);
						handleChangeClient(clientIdValue);
					}}
					value={clients.find(({ id }) => id === formik.values.clientId) || null}
					renderInput={(params) => <TextField {...params} label="Cliente" />}
				/>
				<FormHelperText error>{formik.touched.clientId && formik.errors.clientId}</FormHelperText>
			</FormControl>
			<FormControl fullWidth>
				<Autocomplete
					id="projectId"
					options={projectOptions}
					getOptionLabel={(option) => option.name || ''}
					isOptionEqualToValue={(option, value) => option.id === value?.id}
					filterOptions={(options, { inputValue }) =>
						options.filter(({ name }) => Tools.matchesSearchTerm(inputValue, [name]))
					}
					noOptionsText="Sin resultados"
					onChange={(_, newValue) => formik.setFieldValue('projectId', newValue?.id || '')}
					value={projectOptions.find(({ id }) => id === formik.values.projectId) || null}
					renderInput={(params) => <TextField {...params} label="Proyecto" />}
				/>
				<FormHelperText error>{formik.touched.projectId && formik.errors.projectId}</FormHelperText>
			</FormControl>
			<FormControl fullWidth>
				<InputLabel id="status-label">Estatus</InputLabel>
				<Select
					labelId="status-label"
					id="status"
					label="Estatus"
					name="status"
					onChange={formik.handleChange}
					value={formik.values.status}
				>
					<MenuItem value="">- Todos -</MenuItem>
					{Object.entries(assignmentStatus).map(([key, { label }]) => (
						<MenuItem key={key} value={key}>
							{label}
						</MenuItem>
					))}
				</Select>
				<FormHelperText error>{formik.touched.status && formik.errors.status}</FormHelperText>
			</FormControl>
			<ActionButtons>
				<Button type="submit" color="primary" variant="outlined">
					Buscar
				</Button>
				<Button color="error" variant="outlined" onClick={handleClearFilters}>
					Restablecer
				</Button>
			</ActionButtons>
		</form>
	);
};

export default AssignmentFilterForm;
